/**
 * Palette Configuration
 *
 * Defines light and dark mode palettes using BPS brand colors.
 * Each palette provides semantic color mappings for MUI components.
 *
 * Best Practices:
 * - Use semantic names (primary, secondary, error, etc.)
 * - Maintain WCAG AA contrast ratios
 * - Keep consistent color meanings across modes
 */

import type { PaletteOptions } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import {
  ACTION_ACTIVE,
  ACTION_FOCUS,
  ACTION_HOVER,
  ACTION_SELECTED,
  BPS_BLACK,
  BPS_BLUE,
  BPS_DARK_BLUE,
  BPS_DARK_GREY,
  BPS_GREY,
  BPS_LIGHT_GREY,
  BPS_RED,
  DIVIDER_DARK,
  DIVIDER_LIGHT,
  ERROR_DARK,
  ERROR_LIGHT,
  INFO_DARK,
  INFO_LIGHT,
  NEUTRAL_DARK,
  NEUTRAL_LIGHT,
  PURPLE,
  RAPID_ROBOT_APEX,
  RAPID_ROBOT_CARD_BACKGROUND_DARK,
  RAPID_ROBOT_CARD_BACKGROUND_LIGHT,
  RAPID_ROBOT_CARD_BUTTON_TEXT,
  RAPID_ROBOT_CARD_CHIP_BACKGROUND_DARK,
  RAPID_ROBOT_CARD_CHIP_BACKGROUND_LIGHT,
  RAPID_ROBOT_CARD_TEXT_DARK,
  RAPID_ROBOT_CARD_TEXT_LIGHT,
  RAPID_ROBOT_CREATE_BUTTON_BACKGROUND_DARK,
  RAPID_ROBOT_CREATE_BUTTON_BACKGROUND_LIGHT,
  RAPID_ROBOT_CREATE_BUTTON_HOVER_BACKGROUND_DARK,
  RAPID_ROBOT_CREATE_BUTTON_HOVER_BACKGROUND_LIGHT,
  RAPID_ROBOT_CREATE_BUTTON_TEXT_DARK,
  RAPID_ROBOT_CREATE_BUTTON_TEXT_LIGHT,
  RAPID_ROBOT_DELTA,
  RAPID_ROBOT_FLUX,
  RAPID_ROBOT_MACH,
  RAPID_ROBOT_NOVA,
  RAPID_ROBOT_NOTICE_BACKGROUND_DARK,
  RAPID_ROBOT_NOTICE_BACKGROUND_LIGHT,
  RAPID_ROBOT_NOTICE_TEXT_DARK,
  RAPID_ROBOT_NOTICE_TEXT_LIGHT,
  RAPID_ROBOT_PILL_TEXT,
  RAPID_ROBOT_PIVOT,
  ACCENT_OPAQUE_LIGHT,
  SUCCESS_DARK,
  SUCCESS_LIGHT,
  WARNING_DARK,
  WARNING_LIGHT,
  WARNING_TEXT_DARK,
  WARNING_TEXT_LIGHT,
  WHITE,
  YELLOW,
  ACCENT_OPAQUE_DARK,
  EDITOR_BACKGROUND_DARK,
  EDITOR_BACKGROUND_LIGHT,
  CONSOLE_BACKGROUND_LIGHT,
  CONSOLE_BACKGROUND_DARK,
  EDITOR_TAB_BACKGROUND_DARK,
  EDITOR_TAB_BACKGROUND_LIGHT,
  GAUGE_BASE_COLOR,
  CHART_BLUE,
  CHART_GREEN,
} from './theme-colors';

/**
 * Light mode palette configuration
 */
export const lightPalette: PaletteOptions = {
  primary: {
    main: BPS_BLUE,
  },

  secondary: {
    main: BPS_DARK_BLUE,
  },

  accent: {
    main: BPS_RED,
    hover: ERROR_DARK,
    opaque: ACCENT_OPAQUE_LIGHT,
  },

  error: {
    main: ERROR_LIGHT,
  },

  warning: {
    main: WARNING_LIGHT,
  },

  info: {
    main: INFO_LIGHT,
  },

  success: {
    main: SUCCESS_LIGHT,
  },

  neutral: {
    main: NEUTRAL_LIGHT,
  },

  background: {
    default: WHITE,
    paper: WHITE,
    editor: EDITOR_BACKGROUND_LIGHT,
    console: CONSOLE_BACKGROUND_LIGHT,
    consoleHeader: WHITE,
    appBar: WHITE,
    editorTab: EDITOR_TAB_BACKGROUND_LIGHT,
  },

  action: {
    active: ACTION_ACTIVE,
    hover: ACTION_HOVER,
    selected: ACTION_SELECTED,
    focus: ACTION_FOCUS,
    disabled: 'rgba(0, 0, 0, 0.38)',
    disabledBackground: 'rgba(0, 0, 0, 0.12)',
  },

  spheroWhite: {
    main: WHITE,
  },

  spheroGrey: {
    main: BPS_GREY,
    diagnostic: '#F0F0F0',
  },

  spheroPurple: {
    main: PURPLE,
  },

  spheroYellow: {
    main: YELLOW,
  },

  dashboardGrey: {
    main: BPS_LIGHT_GREY,
  },

  divider: DIVIDER_LIGHT,

  text: {
    primary: BPS_BLUE,
    contrast: WHITE,
    disabled: BPS_GREY,
    warning: WARNING_TEXT_LIGHT,
  },

  controller: {
    stroke: BPS_BLACK,
    hoverFill: ACTION_FOCUS,
    mappedFill: BPS_RED,
    textFill: BPS_BLACK,
    portSlotFill: WHITE,
    portPinFill: BPS_BLACK,
    portUsedFill: BPS_LIGHT_GREY,
    portUsedTextFill: BPS_BLACK,
    portUsedOutlineFill: BPS_BLACK,
  },

  robotIcon: {
    main: WHITE,
  },

  gauge: {
    base: GAUGE_BASE_COLOR,
  },

  chart: {
    blue: CHART_BLUE,
    green: CHART_GREEN,
  },

  rapidRobot: {
    flux: RAPID_ROBOT_FLUX,
    nova: RAPID_ROBOT_NOVA,
    mach: RAPID_ROBOT_MACH,
    pivot: RAPID_ROBOT_PIVOT,
    delta: RAPID_ROBOT_DELTA,
    apex: RAPID_ROBOT_APEX,
    pillText: RAPID_ROBOT_PILL_TEXT,
    cardBackground: RAPID_ROBOT_CARD_BACKGROUND_LIGHT,
    cardText: RAPID_ROBOT_CARD_TEXT_LIGHT,
    cardChipBackground: RAPID_ROBOT_CARD_CHIP_BACKGROUND_LIGHT,
    cardButtonText: RAPID_ROBOT_CARD_BUTTON_TEXT,
    noticeBackground: RAPID_ROBOT_NOTICE_BACKGROUND_LIGHT,
    noticeText: RAPID_ROBOT_NOTICE_TEXT_LIGHT,
    createButtonBackground: RAPID_ROBOT_CREATE_BUTTON_BACKGROUND_LIGHT,
    createButtonHoverBackground: RAPID_ROBOT_CREATE_BUTTON_HOVER_BACKGROUND_LIGHT,
    createButtonText: RAPID_ROBOT_CREATE_BUTTON_TEXT_LIGHT,
  },
};

/**
 * Dark mode palette configuration
 * Maintains brand consistency while providing comfortable dark UI
 */
export const darkPalette: PaletteOptions = {
  primary: {
    main: WHITE,
  },

  secondary: {
    main: BPS_LIGHT_GREY,
  },

  accent: {
    main: BPS_RED,
    hover: ERROR_DARK,
    opaque: ACCENT_OPAQUE_DARK,
  },
  error: {
    main: ERROR_DARK,
  },

  warning: {
    main: WARNING_DARK,
  },

  info: {
    main: INFO_DARK,
  },

  success: {
    main: SUCCESS_DARK,
  },

  neutral: {
    main: NEUTRAL_DARK,
  },

  background: {
    default: BPS_BLACK,
    paper: BPS_BLACK,
    editor: EDITOR_BACKGROUND_DARK,
    console: CONSOLE_BACKGROUND_DARK,
    consoleHeader: BPS_BLACK,
    appBar: BPS_BLACK,
    editorTab: EDITOR_TAB_BACKGROUND_DARK,
  },

  action: {
    active: alpha(WHITE, 0.56),
    hover: alpha(WHITE, 0.08),
    selected: alpha(WHITE, 0.16),
    focus: alpha(WHITE, 0.24),
    disabled: 'rgba(255, 255, 255, 0.38)',
    disabledBackground: 'rgba(255, 255, 255, 0.12)',
  },

  spheroWhite: {
    main: WHITE,
  },

  spheroGrey: {
    main: BPS_DARK_GREY,
    diagnostic: '#181818',
  },

  spheroPurple: {
    main: PURPLE,
  },

  spheroYellow: {
    main: YELLOW,
  },

  dashboardGrey: {
    main: BPS_BLACK,
  },

  divider: DIVIDER_DARK,

  text: {
    primary: WHITE,
    contrast: BPS_BLUE,
    disabled: BPS_GREY,
    warning: WARNING_TEXT_DARK,
  },

  controller: {
    stroke: WHITE,
    hoverFill: alpha(WHITE, 0.24),
    mappedFill: BPS_RED,
    textFill: WHITE,
    portSlotFill: BPS_BLACK,
    portPinFill: WHITE,
    portUsedFill: BPS_LIGHT_GREY,
    portUsedTextFill: BPS_BLACK,
    portUsedOutlineFill: WHITE,
  },

  robotIcon: {
    main: EDITOR_BACKGROUND_DARK,
  },

  gauge: {
    base: GAUGE_BASE_COLOR,
  },

  chart: {
    blue: CHART_BLUE,
    green: CHART_GREEN,
  },

  rapidRobot: {
    flux: RAPID_ROBOT_FLUX,
    nova: RAPID_ROBOT_NOVA,
    mach: RAPID_ROBOT_MACH,
    pivot: RAPID_ROBOT_PIVOT,
    delta: RAPID_ROBOT_DELTA,
    apex: RAPID_ROBOT_APEX,
    pillText: RAPID_ROBOT_PILL_TEXT,
    cardBackground: RAPID_ROBOT_CARD_BACKGROUND_DARK,
    cardText: RAPID_ROBOT_CARD_TEXT_DARK,
    cardChipBackground: RAPID_ROBOT_CARD_CHIP_BACKGROUND_DARK,
    cardButtonText: RAPID_ROBOT_CARD_BUTTON_TEXT,
    noticeBackground: RAPID_ROBOT_NOTICE_BACKGROUND_DARK,
    noticeText: RAPID_ROBOT_NOTICE_TEXT_DARK,
    createButtonBackground: RAPID_ROBOT_CREATE_BUTTON_BACKGROUND_DARK,
    createButtonHoverBackground: RAPID_ROBOT_CREATE_BUTTON_HOVER_BACKGROUND_DARK,
    createButtonText: RAPID_ROBOT_CREATE_BUTTON_TEXT_DARK,
  },
};
